import { Boxes, Plus, RefreshCw, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCustomers } from '@/features/crm/api'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useProjects } from './api'
import { useCatalog, useDeleteCatalogModule, useProductPackages, useSectors } from './catalogApi'
import { CatalogModuleFormModal } from './components/catalog/CatalogModuleFormModal'
import { FeatureList } from './components/catalog/FeatureList'
import { IntegrateModal, type IntegrateTarget } from './components/catalog/IntegrateModal'
import { ModuleCard } from './components/catalog/ModuleCard'
import { PriceMatrix } from './components/catalog/PriceMatrix'
import { ProductPackages } from './components/catalog/ProductPackages'
import { SectorList } from './components/catalog/SectorList'
import { CatalogSyncModal } from '../updates/CatalogSyncModal'
import { CATALOG_WRITE_PERM, PROJECT_PRICING_PERM } from './permissions'
import { MODULE_CATEGORIES, type CatalogModuleView, type ModuleCategory } from './types'
import { toastError } from './ui'

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="bg-card shadow-card rounded-xl border px-4 py-3">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  )
}

const TABS = ['modules', 'features', 'packages', 'prices', 'sectors'] as const
type Tab = (typeof TABS)[number]

/** Ortak modül kataloğu: modüller tanımlanır, projelere entegre edilir; hangi projede hangi modül olduğu görülür. */
export function ProjectCatalogPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const catalog = useCatalog()
  const projects = useProjects()
  const remove = useDeleteCatalogModule()
  const packages = useProductPackages()
  const customers = useCustomers()
  const sectors = useSectors()
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'modules'
  const setTab = (v: Tab) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        if (v === 'modules') n.delete('tab')
        else n.set('tab', v)
        return n
      },
      { replace: true },
    )
  const [q, setQ] = useState('')
  const [category, setCategory] = useState<ModuleCategory | 'all'>('all')
  const [projectId, setProjectId] = useState('all')
  const [form, setForm] = useState<{ open: boolean; module: CatalogModuleView | null }>({ open: false, module: null })
  const [target, setTarget] = useState<IntegrateTarget | null>(null)
  const canWrite = can(PROJECT_PRICING_PERM)
  const canSync = can(CATALOG_WRITE_PERM)
  const [syncOpen, setSyncOpen] = useState(false)

  const all = catalog.data ?? []
  const projectList = projects.data ?? []
  const nameOf = useMemo(() => new Map((catalog.data ?? []).map((m) => [m.id, m.name])), [catalog.data])
  const term = q.trim().toLocaleLowerCase('tr')
  const modules = all.filter(
    (m) =>
      (category === 'all' || m.category === category) &&
      (projectId === 'all' ||
        (projectId === 'none' ? m.usage.length === 0 : m.usage.some((u) => u.projectId === projectId))) &&
      (!term || `${m.name} ${m.code} ${m.description}`.toLocaleLowerCase('tr').includes(term)),
  )

  const onDelete = async (m: CatalogModuleView) => {
    const ok = await confirmModal({
      title: t('projects.catalog.deleteTitle'),
      description: t('projects.catalog.deleteBody', { name: m.name }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(m.id)
      toast.success(t('projects.catalog.deleted'))
    } catch (e) {
      const details = isApiError(e) ? (e.details as { projects?: string[]; modules?: string[] } | undefined) : undefined
      const names = details?.projects ?? details?.modules
      if (isApiError(e) && names) toast.error(t(`errors.${e.code}`), { description: names.join(', ') })
      else toastError(e)
    }
  }

  const showModules = (id: string) => {
    setProjectId(id)
    setCategory('all')
    setQ('')
    setTab('modules')
  }

  const loading = catalog.isLoading || projects.isLoading
  const error = catalog.error ?? projects.error

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.projects')}
        title={t('projects.catalog.title')}
        description={t('projects.catalog.subtitle')}
        actions={
          canWrite || canSync ? (
            <>
              {canSync ? (
                <Button variant="outline" onClick={() => setSyncOpen(true)}>
                  <RefreshCw />
                  {t('updates.catalog.open')}
                </Button>
              ) : null}
              {canWrite ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setTarget({ kind: 'create' })}
                    disabled={!all.length || !projectList.length}
                  >
                    {t('projects.catalog.addToProject')}
                  </Button>
                  <Button onClick={() => setForm({ open: true, module: null })}>
                    <Plus />
                    {t('projects.catalog.newModule')}
                  </Button>
                </>
              ) : null}
            </>
          ) : null
        }
      />

      {error ? (
        <div className="bg-card rounded-xl border">
          <ErrorState error={error} onRetry={() => void Promise.all([catalog.refetch(), projects.refetch()])} />
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat value={all.length} label={t('projects.catalog.stats.modules')} />
            <Stat value={all.filter((m) => m.active).length} label={t('projects.catalog.stats.active')} />
            <Stat value={all.filter((m) => m.usage.length > 0).length} label={t('projects.catalog.stats.integrated')} />
            <Stat value={all.reduce((sum, m) => sum + m.usage.length, 0)} label={t('projects.catalog.stats.links')} />
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              {TABS.map((k) => (
                <TabsTrigger key={k} value={k}>
                  {t(`projects.catalog.tabs.${k}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {tab === 'features' ? (
            <FeatureList catalog={all} projects={projectList} canWrite={canWrite} />
          ) : tab === 'packages' ? (
            packages.data ? (
              <ProductPackages
                projects={projectList}
                packages={packages.data}
                catalog={all}
                customers={customers.data ?? []}
                sectors={sectors.data ?? []}
                canWrite={canWrite}
                onShowModules={showModules}
                onIntegrate={setTarget}
              />
            ) : (
              <Skeleton className="h-64 rounded-xl" />
            )
          ) : tab === 'prices' ? (
            <PriceMatrix catalog={all} projects={projectList} canWrite={canWrite} />
          ) : tab === 'sectors' ? (
            <SectorList sectors={sectors.data ?? []} projects={projectList} canWrite={canWrite} />
          ) : (
            <>
              <div className="bg-card shadow-card flex flex-col gap-2 rounded-xl border p-3 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1 lg:max-w-xs">
                  <Search
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                    aria-hidden
                  />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('projects.catalog.search')}
                    aria-label={t('projects.catalog.search')}
                    className="pl-8"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 lg:flex">
                  <Select
                    className="lg:w-48"
                    value={category}
                    onChange={(v) => setCategory(v as ModuleCategory | 'all')}
                    aria-label={t('projects.catalog.fields.category')}
                    options={[
                      { value: 'all', label: t('projects.catalog.allCategories') },
                      ...MODULE_CATEGORIES.map((c) => ({ value: c, label: t(`projects.moduleCategory.${c}`) })),
                    ]}
                  />
                  <Select
                    className="lg:w-52"
                    value={projectId}
                    onChange={setProjectId}
                    aria-label={t('projects.catalog.project')}
                    options={[
                      { value: 'all', label: t('projects.catalog.allProjects') },
                      { value: 'none', label: t('projects.catalog.unused') },
                      ...projectList.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                </div>
              </div>

              {modules.length === 0 ? (
                <div className="bg-card shadow-card rounded-xl border">
                  <EmptyState
                    icon={Boxes}
                    title={all.length ? t('projects.catalog.noMatch') : t('projects.catalog.empty')}
                    description={all.length ? undefined : t('projects.catalog.emptyHint')}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {modules.map((m) => (
                    <ModuleCard
                      key={m.id}
                      module={m}
                      nameOf={nameOf}
                      projects={projectList}
                      canWrite={canWrite}
                      onEdit={() => setForm({ open: true, module: m })}
                      onDelete={() => void onDelete(m)}
                      onIntegrate={setTarget}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <CatalogModuleFormModal
        open={form.open}
        module={form.module}
        catalog={all}
        onOpenChange={(open) => setForm((s) => ({ ...s, open }))}
      />
      <IntegrateModal target={target} catalog={all} projects={projectList} onClose={() => setTarget(null)} />
      {syncOpen ? <CatalogSyncModal onClose={() => setSyncOpen(false)} /> : null}
    </>
  )
}
